<template>
    <div ref="root" class="projects-table-root">
        <v-card class="projects-table elevation-2">
            <v-data-table
                class="projects-table__grid"
                :class="{ 'projects-table__grid--constrained': isConstrained }"
                :headers="computedHeaders"
                :items="sortedItems"
                @click:row="handleRowClick"
                :hide-default-footer="true"
                :fixed-header="isConstrained"
                :height="constrainedHeight"
                items-per-page="-1"
                :sort-by="sortByConfig"
            >
            <!-- Custom header for project/projectName/userName column with sorting menu (always shown) -->
            <template #header.project="{ column }">
                <div
                    class="d-flex align-center justify-start flex-nowrap table-header-controls"
                >
                    <span class="flex-shrink-0">{{ column.title }}</span>
                    <TableSortMenu
                        v-model="sortKey"
                        custom-option-key="Importance"
                        custom-option-label="Importance"
                        :show-custom-option="enableReordering"
                        @click.stop
                    />
                    <TableHeaderSearch
                        v-model="searchQuery"
                        v-model:open="searchOpen"
                    />
                </div>
            </template>
            <template #header.projectName="{ column }">
                <div
                    class="d-flex align-center justify-start flex-nowrap table-header-controls"
                >
                    <span class="flex-shrink-0">{{ column.title }}</span>
                    <TableSortMenu
                        v-model="sortKey"
                        custom-option-key="Importance"
                        custom-option-label="Importance"
                        :show-custom-option="enableReordering"
                        @click.stop
                    />
                    <TableHeaderSearch
                        v-model="searchQuery"
                        v-model:open="searchOpen"
                    />
                </div>
            </template>
            <template #header.userName="{ column }">
                <div
                    class="d-flex align-center justify-start flex-nowrap table-header-controls"
                >
                    <span class="flex-shrink-0">{{ column.title }}</span>
                    <TableSortMenu
                        v-model="sortKey"
                        custom-option-key="Importance"
                        custom-option-label="Importance"
                        :show-custom-option="enableReordering"
                        @click.stop
                    />
                    <TableHeaderSearch
                        v-model="searchQuery"
                        v-model:open="searchOpen"
                    />
                </div>
            </template>

            <template v-if="showOdooExport" #header.odooExport>
                <div class="d-flex justify-end align-center" @click.stop>
                    <v-btn
                        class="d-none d-sm-inline-flex"
                        color="primary"
                        variant="tonal"
                        size="small"
                        prepend-icon="mdi-file-download"
                        @click="$emit('export-odoo')"
                    >
                        Export for Odoo
                    </v-btn>
                    <v-btn
                        class="d-inline-flex d-sm-none"
                        color="primary"
                        variant="tonal"
                        icon
                        size="small"
                        aria-label="Export for Odoo"
                        @click="$emit('export-odoo')"
                    >
                        <v-icon>mdi-file-download</v-icon>
                    </v-btn>
                </div>
            </template>

            <!-- Body with draggable support - only render when there are items, otherwise let Vuetify use default no-data slot -->
            <template
                #body="{ items }"
                v-if="sortedItems && sortedItems.length > 0"
            >
                <!-- Draggable wrapper when reordering is enabled -->
                <draggable
                    v-if="isReorderEnabled && enableReordering"
                    v-model="reorderedItems"
                    itemKey="id"
                    @end="onOrderChange"
                    tag="tbody"
                    class="reorder-table"
                >
                    <template #item="{ element }">
                        <tr
                            :key="element.id"
                            class="reorder-element"
                            @click="
                                enableClickNavigation
                                    ? handleRowClick(element, {
                                          item: element,
                                      })
                                    : null
                            "
                            :class="{
                                'project-row--inactive':
                                    isInactiveProjectRow(element),
                            }"
                            style="cursor: pointer"
                        >
                            <td>
                                <div class="mt-1">
                                    <v-icon class="mr-1"
                                        >mdi-arrow-split-horizontal</v-icon
                                    >
                                    {{ projectRowLabel(element) }}
                                </div>
                                <ul
                                    v-if="
                                        element.description &&
                                        element.description.length
                                    "
                                    class="ml-2 mb-2 pl-4"
                                >
                                    <li
                                        v-for="(
                                            description, index
                                        ) in element.description"
                                        :key="index"
                                        style="font-style: italic"
                                    >
                                        {{ description }}
                                    </li>
                                </ul>
                            </td>
                            <td v-if="showHours">
                                {{
                                    element.totalHours ||
                                    element.totalTimeWorked
                                }}
                            </td>
                            <td v-if="showOvertime">
                                {{
                                    element.overtime ||
                                    element.overtimeTimeWorked ||
                                    "0h:0m"
                                }}
                            </td>
                            <td v-if="enablePriority && canEditPriority">
                                <div @click.stop>
                                    <v-select
                                        v-model="element.priority"
                                        :items="priorityOptionsList"
                                        variant="plain"
                                        @update:modelValue="
                                            (newValue) =>
                                                handlePriorityUpdate(
                                                    element,
                                                    newValue
                                                )
                                        "
                                        density="compact"
                                        hide-details="true"
                                        max-width="100px"
                                    ></v-select>
                                </div>
                            </td>
                            <td v-if="showOdooExport"></td>
                        </tr>
                    </template>
                </draggable>
                <!-- Normal rendering when not reordering -->
                <template v-else>
                    <tr
                        v-for="item in items"
                        :key="item.id || item.projectId"
                        :class="{
                            'project-row--inactive': isInactiveProjectRow(item),
                        }"
                        @click="
                            enableClickNavigation
                                ? handleRowClick(item, { item })
                                : null
                        "
                        :style="rowStyle(item)"
                    >
                        <td>
                            <div class="mt-1">
                                <v-tooltip
                                    v-if="
                                        enableClickNavigation &&
                                        navigationRoute === 'ProjectInfo' &&
                                        (item.projectId || item.id) &&
                                        !canAccessProject(
                                            item.projectId || item.id
                                        )
                                    "
                                    location="top"
                                    :open-delay="1000"
                                >
                                    <template #activator="{ props }">
                                        <span
                                            v-bind="props"
                                            style="cursor: not-allowed"
                                        >
                                            {{ projectRowLabel(item) }}
                                        </span>
                                    </template>
                                    <span>
                                        You do not have permissions to navigate
                                        to this project
                                    </span>
                                </v-tooltip>
                                <span v-else>
                                    {{ projectRowLabel(item) }}
                                </span>
                            </div>
                            <ul
                                v-if="
                                    item.description && item.description.length
                                "
                                class="ml-2 mb-2 pl-4"
                            >
                                <li
                                    v-for="(
                                        description, index
                                    ) in item.description"
                                    :key="index"
                                    style="font-style: italic"
                                >
                                    {{ description }}
                                </li>
                            </ul>
                        </td>
                        <td v-if="showHours">
                            <ElapsedTimeDisplay
                                v-if="
                                    showActiveEntryTime &&
                                    item.activeEntryStartTime
                                "
                                :startTime="item.activeEntryStartTime"
                            />
                            <span v-else>
                                {{ item.totalHours || item.totalTimeWorked }}
                            </span>
                        </td>
                        <td v-if="showOvertime">
                            {{
                                item.overtime ||
                                item.overtimeTimeWorked ||
                                "0h:0m"
                            }}
                        </td>
                        <td v-if="enablePriority && canEditPriority">
                            <div @click.stop>
                                <v-select
                                    v-model="item.priority"
                                    :items="priorityOptionsList"
                                    variant="plain"
                                    @update:modelValue="
                                        (newValue) =>
                                            handlePriorityUpdate(item, newValue)
                                    "
                                    density="compact"
                                    hide-details="true"
                                    max-width="100px"
                                ></v-select>
                            </div>
                        </td>
                        <td v-if="showOdooExport"></td>
                    </tr>
                </template>
            </template>

                <!-- Totals stay in the real table so columns align; sticky keeps them visible while scrolling -->
                <template v-if="showTotalsFooter" #body.append>
                    <tr class="projects-table-totals">
                        <td>Total hours worked:</td>
                        <td v-if="showHours">{{ totalTimeWorked }}</td>
                        <td v-if="showOvertime">{{ totalOvertimeWorked }}</td>
                        <td v-if="enablePriority && canEditPriority"></td>
                        <td v-if="showOdooExport"></td>
                    </tr>
                </template>
            </v-data-table>
        </v-card>
        <!-- Snackbar for Notifications -->
        <MessageSnackbar
            :message="snackbar.message"
            :color="snackbar.color"
            :timeout="snackbar.timeout"
            :show="snackbar.show"
            @update:show="snackbar.show = $event"
        />
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import MessageSnackbar from "./MessageSnackbar.vue";
import { triggerSnackbar, isBelowThreshold } from "@/utils/utils";
import {
    canAccessProject as canAccessProjectHelper,
    compareActiveProjectsFirst,
    formatProjectLabel,
    isProjectActive,
} from "@/utils/projectUtils";
import draggable from "vuedraggable";
import dayjs from "@/plugins/dayjs";
import ElapsedTimeDisplay from "./ElapsedTimeDisplay.vue";
import TableSortMenu from "./TableSortMenu.vue";
import TableHeaderSearch from "./TableHeaderSearch.vue";

export default {
    components: {
        MessageSnackbar,
        draggable,
        ElapsedTimeDisplay,
        TableSortMenu,
        TableHeaderSearch,
    },
    props: {
        /** Drives the branch between RTDB vs Firestore */
        selectedPeriod: {
            type: String,
            default: "today",
        },
        /** The shaped rows coming back from Reports.vue */
        reportRows: {
            type: Array,
            default: () => [],
        },
        /** External table data (for UserInfo use case) - if provided, uses this instead of computing */
        tableData: {
            type: Array,
            default: null,
        },
        /** Custom headers - if provided, uses these instead of computing */
        headers: {
            type: Array,
            default: null,
        },
        /** Enable reordering functionality */
        enableReordering: {
            type: Boolean,
            default: false,
        },
        /** Enable priority editing */
        enablePriority: {
            type: Boolean,
            default: true,
        },
        /** Enable row click navigation */
        enableClickNavigation: {
            type: Boolean,
            default: true,
        },
        /** Show Export for Odoo control in the rightmost header */
        showOdooExport: {
            type: Boolean,
            default: false,
        },
        /** Route name for navigation (e.g., 'ProjectInfo') */
        navigationRoute: {
            type: String,
            default: "ProjectInfo",
        },
        /** Parameter name for navigation (e.g., 'projectId') */
        navigationParam: {
            type: String,
            default: "projectId",
        },
        /** Current tab (for sorting UI) */
        currentTab: {
            type: String,
            default: "projects",
        },
        /** Table height */
        tableHeight: {
            type: String,
            default: "",
        },
        /** Total time worked (for footer) */
        totalTimeWorked: {
            type: String,
            default: null,
        },
        /** Total overtime worked (for footer) */
        totalOvertimeWorked: {
            type: String,
            default: null,
        },
        /** Show active entry elapsed time (only used in ProjectInfo) */
        showActiveEntryTime: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["update-priority", "row-click", "order-changed", "export-odoo"],
    /**
     * Component's reactive data properties.
     * @returns {Object} An object containing the component's reactive state.
     */
    data() {
        return {
            priorityOptions: ["High", "Normal", "Low"], // Priority options for project-level
            userPriorityOptions: ["Highest", "Normal", "Lowest"], // Priority options for user-level
            snackbar: {
                show: false,
                message: "",
            },
            sortOrder: "asc",
            isReorderEnabled: false,
            sortKey: "A-Z", // Default to "A-Z" (alphabetical ascending)
            searchQuery: "",
            searchOpen: false,
            reorderedItems: [],
            /** Pixel height passed to v-data-table only when content overflows */
            constrainedHeight: undefined,
            _measureRaf: null,
            _resizeObserver: null,
        };
    },
    computed: {
        isConstrained() {
            return this.constrainedHeight != null;
        },
        isNrOfItems() {
            const source = this.enableReordering
                ? this.reorderedItems
                : this.sortedItems;
            return isBelowThreshold(source);
        },
        /**
         * Computed properties derived from Vuex store or internal data.
         */
        ...mapGetters([
            "getUsers", // Fetches the list of users.
            "getManagedProjects", // Fetches the list of managed projects.
            "getRights", // Fetches the rights or roles of the current user.
            "getCompanies", // Fetches the list of  client companies.
        ]),

        /**
         * Checks if the current viewer can access a specific project.
         * - Admins (viewProjects includes "all") can access everything.
         * - Team leads/operators: only projects in getManagedProjects.
         */
        canAccessProject() {
            return (projectId) =>
                canAccessProjectHelper(
                    projectId,
                    this.getRights,
                    this.getManagedProjects
                );
        },

        /**
         * Returns the appropriate priority options based on context
         */
        priorityOptionsList() {
            // Use user priority options if this is for user projects (has projectName field)
            // Otherwise use project priority options
            return this.enableReordering
                ? this.userPriorityOptions
                : this.priorityOptions;
        },

        /**
         * Generates headers for the table based on props or user permissions.
         * @returns {Array<Object>} Array of header objects for the table.
         */
        computedHeaders() {
            // Use provided headers if available, but filter out overtime if not needed
            if (this.headers) {
                // Filter out overtime header if there's no meaningful overtime data
                let headers = this.headers;
                if (!this.showOvertime) {
                    headers = this.headers.filter(
                        (header) =>
                            header.value !== "overtime" &&
                            header.value !== "overtimeTimeWorked"
                    );
                }
                if (
                    this.showOdooExport &&
                    !headers.some((h) => h.value === "odooExport")
                ) {
                    return [
                        ...headers,
                        {
                            title: "",
                            value: "odooExport",
                            sortable: false,
                            width: "140",
                            align: "end",
                        },
                    ];
                }
                return headers;
            }

            // Determine which field names to use based on data format
            const hasUserName =
                this.computedTableData.length > 0 &&
                this.computedTableData.some((item) => item.userName);
            const hasProjectName =
                this.computedTableData.length > 0 &&
                this.computedTableData.some((item) => item.projectName);
            const hasTotalTimeWorked =
                this.computedTableData.length > 0 &&
                this.computedTableData.some((item) => item.totalTimeWorked);

            // Check if we have userName (for user tables) or projectName/project (for project tables)
            const projectField = hasUserName
                ? "userName"
                : hasProjectName
                ? "projectName"
                : "project";
            const hoursField = hasTotalTimeWorked
                ? "totalTimeWorked"
                : "totalHours";
            const overtimeField = hasTotalTimeWorked
                ? "overtimeTimeWorked"
                : "overtime";

            const baseHeaders = [
                {
                    title: "Projects",
                    value: projectField,
                    width: "35",
                    sortable:
                        !this.enableReordering ||
                        this.currentTab !== "projects",
                },
            ];

            if (this.showHours) {
                baseHeaders.push({
                    title: "Hours",
                    value: hoursField,
                    width: "20",
                });

                // Only add overtime header if there's meaningful overtime data
                if (this.showOvertime) {
                    baseHeaders.push({
                        title: "Overtime",
                        value: overtimeField,
                        width: "20",
                    });
                }
            }

            if (this.enablePriority && this.canEditPriority) {
                baseHeaders.push({
                    title: "Priority",
                    value: "priority",
                    width: "25",
                    sortable: !this.enableReordering,
                });
            }

            if (this.showOdooExport) {
                baseHeaders.push({
                    title: "",
                    value: "odooExport",
                    sortable: false,
                    width: "140",
                    align: "end",
                });
            }

            return baseHeaders;
        },

        /**
         * Gets the default field to sort by (project name field) for Vuetify's sort-by prop.
         * @returns {string|null} The field name to sort by, or null if headers aren't ready.
         */
        defaultSortField() {
            if (
                this.computedHeaders.length > 0 &&
                this.computedHeaders[0].value
            ) {
                return this.computedHeaders[0].value;
            }
            return null;
        },

        /**
         * Determines the sort-by configuration for Vuetify data table.
         * @returns {Array<Object>|undefined} Sort configuration or undefined for custom sorting.
         */
        sortByConfig() {
            if (!this.shouldUseVuetifySort) {
                return undefined;
            }
            
            const field = this.defaultSortField;
            if (!field) return undefined;

            if (this.sortKey === "A-Z") {
                return [{ key: field, order: "asc" }];
            } else if (this.sortKey === "Z-A") {
                return [{ key: field, order: "desc" }];
            }
            
            // Default to ascending
            return [{ key: field, order: "asc" }];
        },

        /**
         * Sorting is handled in sortedItems so inactive projects can stay last.
         * @returns {boolean}
         */
        shouldUseVuetifySort() {
            return false;
        },

        /**
         * Determines what columns to show
         */
        showHours() {
            if (this.canViewTotalHours) return true;
            if (this.tableData) {
                return this.tableData.some(
                    (item) => item.totalHours || item.totalTimeWorked
                );
            }
            return this.computedTableData.some(
                (item) => item.totalHours || item.totalTimeWorked
            );
        },
        showOvertime() {
            if (!this.canViewTotalHours) return false;

            // Check sortedItems (what's actually displayed) for better reactivity
            // This ensures the header updates immediately when data changes
            const dataToCheck =
                this.sortedItems ||
                this.tableData ||
                this.computedTableData ||
                [];
            return dataToCheck.some((item) => {
                const overtime = item.overtime || item.overtimeTimeWorked || "";
                // Meaningful overtime means: not empty, not "0h:0m", not "0h:0min"
                return (
                    overtime &&
                    overtime !== "0h:0m" &&
                    overtime !== "0h:0min" &&
                    overtime !== "N/A" &&
                    overtime.trim() !== ""
                );
            });
        },
        showTotalsFooter() {
            return (
                !!(this.totalTimeWorked || this.totalOvertimeWorked) &&
                this.sortedItems &&
                this.sortedItems.length > 0
            );
        },

        /**
         * Determines the data to display in the table.
         * Uses external tableData if provided, otherwise computes from props/store.
         * @returns {Array<Object>} Table data.
         */
        computedTableData() {
            // If external tableData is provided, use it
            if (this.tableData) {
                return this.tableData;
            }

            // 1) RTDB branch for Today/Yesterday
            if (
                ["today", "yesterday"].includes(this.selectedPeriod) &&
                this.canViewTotalHours
            ) {
                return this.calculateProjectWorkHours;
            }
            // 1) Always start from the full list of managed projects
            const managedProjects = (this.getManagedProjects || []).filter(
                (proj) => proj.projectId !== "SYSTEM_LUNCH-BREAK"
            );

            // 2) Turn your existing reportRows into a lookup
            const reportRowsById = this.reportRows.reduce((managed, row) => {
                managed[row.projectId] = row;
                return managed;
            }, {});

            // 3) Build each table row, defaulting to zeros if no data in that period
            return managedProjects.map((proj) => {
                const row = reportRowsById[proj.projectId];
                return {
                    projectId: proj.projectId,
                    project: proj.name,
                    totalHours: this.canViewTotalHours
                        ? row?.totalHours || "0h:0m"
                        : "",
                    overtime: this.canViewTotalHours
                        ? row?.overtimeHours || "0h:0m"
                        : "",
                    priority: row?.priority || "Normal",
                    isActive: isProjectActive(proj),
                };
            });
        },

        /**
         * Sorted rows (no text search). Cached unless table data / sortKey change.
         */
        sortedItemsBase() {
            const nameOf = (item) =>
                String(
                    item?.project ||
                        item?.projectName ||
                        item?.userName ||
                        ""
                );
            const rows = [...(this.computedTableData || [])];

            return rows.sort((a, b) => {
                const byActive = compareActiveProjectsFirst(a, b);
                if (byActive !== 0) return byActive;

                if (this.sortKey === "Importance") {
                    if (a.order != null && b.order != null) {
                        return a.order - b.order;
                    }
                    if (a.order != null) return -1;
                    if (b.order != null) return 1;
                    return 0;
                }

                const cmp = nameOf(a).localeCompare(nameOf(b));
                return this.sortKey === "Z-A" ? -cmp : cmp;
            });
        },

        /**
         * Table items: sorted list filtered by header search only.
         */
        sortedItems() {
            const q = (this.searchQuery || "").trim().toLowerCase();
            const rows = this.sortedItemsBase;
            if (!q) return rows;
            return rows.filter((item) => {
                const name = String(
                    item?.project ||
                        item?.projectName ||
                        item?.userName ||
                        ""
                ).toLowerCase();
                return name.includes(q);
            });
        },

        /**
         * Checks if the user has permission to view total project hours.
         * @returns {boolean} `true` if the user can view total hours, `false` otherwise.
         */
        canViewTotalHours() {
            return this.getRights?.viewProjectsTotalHours || false;
        },

        /**
         * Checks if the user has permission to edit project priorities.
         * @returns {boolean} `true` if the user can edit priorities, `false` otherwise.
         */
        canEditPriority() {
            // For user projects, check editUserProjectPriority
            if (this.enableReordering) {
                return this.getRights?.editUserProjectPriority || false;
            }
            // For project-level, check editProjectPriority
            return this.getRights?.editProjectPriority || false;
        },

        /**
         * Computes the total hours worked for all managed projects.
         * Includes all projects, even if no hours were logged.
         *
         * @returns {Array} - A list of managed projects with their total hours worked.
         * Each project object contains:
         *  - projectId: The unique ID of the project.
         *  - project: The name of the project.
         *  - totalHours: Total hours worked on the project (0 if no hours were logged).
         */
        calculateProjectWorkHours() {
            const projectsArray = this.getManagedProjects || [];
            const users = this.getUsers || [];

            // 1) build our day window
            const now = dayjs();
            const day =
                this.selectedPeriod === "yesterday"
                    ? now.subtract(1, "day")
                    : now;
            const dateStart = day.startOf("day").utc().valueOf(); // ms
            const dateEnd = day.endOf("day").utc().valueOf(); // ms

            // 2) init accumulators (ms)
            const projectWorkData = projectsArray.reduce((acc, project) => {
                if (project.projectId !== "SYSTEM_LUNCH-BREAK") {
                    acc[project.projectId] = {
                        projectId: project.projectId,
                        project: project.name,
                        totalMs: 0,
                        overtimeMs: 0,
                        startTime: null,
                        priority: project.priority || "Normal",
                        isActive: isProjectActive(project),
                    };
                }
                return acc;
            }, {});

            // 3) aggregate only entries whose endTime lives in [dateStart, dateEnd]
            users.forEach((user) => {
                const entries = Array.isArray(user.timeEntries)
                    ? user.timeEntries
                    : user.timeEntries && typeof user.timeEntries === "object"
                    ? Object.values(user.timeEntries)
                    : [];

                entries.forEach((entry) => {
                    const startMs = entry.startTime;
                    const endMs = entry.endTime;

                    // completed entries in our window?
                    if (
                        endMs != null &&
                        projectWorkData[entry.projectId] &&
                        endMs >= dateStart &&
                        endMs <= dateEnd
                    ) {
                        const difference = endMs - startMs;
                        projectWorkData[entry.projectId].totalMs += difference;

                        if (entry.overtime === true) {
                            projectWorkData[entry.projectId].overtimeMs +=
                                difference;
                        }
                    }

                    // preserve your "active" logic
                    if (
                        endMs == null &&
                        startMs != null &&
                        projectWorkData[entry.projectId]
                    ) {
                        projectWorkData[entry.projectId].startTime = startMs;
                    }
                });
            });

            // 4) format back to "Hh:Mm"
            return Object.values(projectWorkData).map((data) => {
                const totalMinutes = Math.round(data.totalMs / 60000);
                const hours = Math.floor(totalMinutes / 60);
                const minutes = totalMinutes % 60;

                const totalOvertimeMinutes = Math.round(
                    data.overtimeMs / 60000
                );
                const overtimeHours = Math.floor(totalOvertimeMinutes / 60);
                const overtimeMinutes = totalOvertimeMinutes % 60;
                return {
                    projectId: data.projectId,
                    project: data.project,
                    totalHours: `${hours}h:${minutes}m`,
                    overtime: `${overtimeHours}h:${overtimeMinutes}m`,
                    startTime: data.startTime,
                    priority: data.priority,
                    isActive: data.isActive !== false,
                };
            });
        },
    },
    watch: {
        /**
         * Watches for changes in computedTableData and updates reorderedItems accordingly.
         */
        computedTableData: {
            immediate: true,
            handler(newData) {
                if (this.enableReordering) {
                    // Add order property if not present, using array index as fallback
                    this.reorderedItems = newData.map((item, index) => ({
                        ...item,
                        order: item.order ?? index + 1,
                    }));
                }
            },
        },
        /**
         * Watches for changes in sortKey and updates reordering state accordingly.
         */
        sortKey(newValue) {
            if (newValue === "A-Z" || newValue === "Z-A") {
                this.isReorderEnabled = false;
                // Reset reorderedItems to match current computedTableData when switching to alphabetical
                if (this.enableReordering) {
                    this.reorderedItems = this.computedTableData.map(
                        (item, index) => ({
                            ...item,
                            order: item.order ?? index + 1,
                        })
                    );
                }
            } else if (newValue === "Importance") {
                this.isReorderEnabled = true;
            }
        },
        sortedItems: {
            handler() {
                this.scheduleMeasureTableHeight();
            },
            deep: true,
        },
        showTotalsFooter() {
            this.scheduleMeasureTableHeight();
        },
        computedHeaders: {
            handler() {
                this.scheduleMeasureTableHeight();
            },
            deep: true,
        },
    },
    mounted() {
        if (this.enableReordering) {
            // Add order property if not present, using array index as fallback
            this.reorderedItems = this.computedTableData.map((item, index) => ({
                ...item,
                order: item.order ?? index + 1,
            }));
        }
        if (typeof ResizeObserver !== "undefined" && this.$refs.root) {
            this._resizeObserver = new ResizeObserver(() => {
                this.scheduleMeasureTableHeight();
            });
            this._resizeObserver.observe(this.$refs.root);
        }
        this.scheduleMeasureTableHeight();
    },
    beforeUnmount() {
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
        if (this._measureRaf != null) {
            cancelAnimationFrame(this._measureRaf);
            this._measureRaf = null;
        }
    },
    /**
     * Methods for handling user interactions and navigation.
     */
    methods: {
        scheduleMeasureTableHeight() {
            if (this._measureRaf != null) {
                cancelAnimationFrame(this._measureRaf);
            }
            this._measureRaf = requestAnimationFrame(() => {
                this._measureRaf = null;
                this.measureTableHeight();
            });
        },
        /**
         * Use Vuetify fixed-header + height only when content exceeds the
         * available parent height. Short/empty tables keep natural height.
         */
        measureTableHeight() {
            const root = this.$refs.root;
            if (!root) return;

            // Available space = parent fill area (root is height:100%), minus padding
            // so the card elevation stays visible.
            const styles = window.getComputedStyle(root);
            const padY =
                (parseFloat(styles.paddingTop) || 0) +
                (parseFloat(styles.paddingBottom) || 0);
            const available = Math.floor(root.clientHeight - padY);
            if (available <= 0) {
                if (this.constrainedHeight !== undefined) {
                    this.constrainedHeight = undefined;
                }
                return;
            }

            const table = root.querySelector(".v-table__wrapper > table");
            if (!table) {
                if (this.constrainedHeight !== undefined) {
                    this.constrainedHeight = undefined;
                }
                return;
            }

            const natural = table.scrollHeight;
            const next = natural > available + 1 ? available : undefined;

            if (this.constrainedHeight !== next) {
                this.constrainedHeight = next;
            }
        },

        /**
         * Updates the project's priority and emits event.
         * @param {Object} item - The project item.
         * @param {string} newPriority - The new priority value.
         */
        async handlePriorityUpdate(item, newPriority) {
            const oldPriority = item.priority;
            item.priority = newPriority;

            // Emit event for parent to handle
            this.$emit("update-priority", { item, newPriority });

            // If this is project-level priority (not user-level), update in store
            if (!this.enableReordering) {
                try {
                    await this.$store.dispatch("updateProjectInStore", {
                        projectId: item.projectId || item.id,
                        updatedProject: { priority: newPriority },
                    });
                } catch (error) {
                    console.error("Failed to update priority:", error);
                    // Revert the priority change in the local item if the update fails
                    item.priority = oldPriority;
                }
            }
        },

        /**
         * Handles row click - navigates or emits event.
         * @param {Object} item - The clicked item.
         * @param {Object} event - The event object.
         */
        handleRowClick(item, event) {
            if (!this.enableClickNavigation) {
                return;
            }

            // Prefer the Vuetify-wrapped row (`event.item`), fall back to raw `item`
            const src = (event && event.item) || item || {};

            let id = null;

            if (this.navigationRoute === "ProjectInfo") {
                // Project navigation: use projectId / id
                id = src.projectId || src.id;

                // Enforce permission before navigating to ProjectInfo
                if (id && !this.canAccessProject(id)) {
                    return;
                }
            } else if (this.navigationRoute === "UserInfo") {
                // User navigation: use userId / uid
                id = src.userId || src.id;
            } else {
                // Fallback for any other routes – reasonable defaults
                id = src.id || src.projectId || src.userId || src.uid;
            }

            if (!id) {
                console.error("ID is missing in eventData.item", {
                    item,
                    event,
                });
                return;
            }

            // Emit event for parent to handle
            this.$emit("row-click", { item, event });

            // Navigate
            this.$router.push({
                name: this.navigationRoute,
                params: { [this.navigationParam]: id },
            });
        },

        isInactiveProjectRow(item) {
            if (!item || item.userName) return false;
            if (typeof item.isActive === "boolean") return !item.isActive;
            return false;
        },
        projectRowLabel(item) {
            if (item?.userName && !item?.project && !item?.projectName) {
                return item.userName;
            }
            const name = item?.project || item?.projectName || item?.userName || "";
            if (typeof item?.isActive === "boolean") {
                return formatProjectLabel(name, item.isActive);
            }
            return name;
        },
        rowStyle(item) {
            const inaccessible =
                this.enableClickNavigation &&
                this.navigationRoute === "ProjectInfo" &&
                (item.projectId || item.id) &&
                !this.canAccessProject(item.projectId || item.id);
            if (inaccessible) {
                return "cursor: not-allowed; opacity: 0.6";
            }
            if (this.enableClickNavigation) {
                return "cursor: pointer";
            }
            return "";
        },

        /**
         * Emits an event to update the tableData when the item order changes.
         */
        onOrderChange() {
            const updatedOrder = this.reorderedItems.map((item, index) => ({
                ...item,
                order: index + 1, // Assign a new order based on the current drag-and-drop position
            }));
            this.$emit("order-changed", updatedOrder);
        },
    },
};
</script>
<style scoped>
.table-header-controls {
    position: relative;
    overflow: visible;
}

.projects-table__grid :deep(th) {
    overflow: visible;
}

/* Fill parent for overflow measurement; padding keeps card elevation visible */
.projects-table-root {
    width: 100%;
    height: 100%;
    max-height: 100%;
    min-height: 0;
    padding: 4px;
    box-sizing: border-box;
    overflow: hidden;
}

.projects-table {
    max-height: 100%;
}

.project-row--inactive {
    opacity: 0.55;
    color: rgba(var(--v-theme-on-surface), 0.7);
}

/* Sticky totals inside Vuetify's scroll wrapper (active when height-constrained) */
.projects-table__grid--constrained :deep(tr.projects-table-totals td) {
    position: sticky;
    bottom: 0;
    z-index: 2;
    font-weight: 500;
    background: rgb(var(--v-theme-surface));
    box-shadow: 0 -1px 0 rgba(var(--v-border-color), var(--v-border-opacity));
}

.v-select::v-deep(.v-select__selection-text) {
    font-size: 0.875rem !important;
}

.draggable-helper {
    cursor: move;
}

.reorder-table {
    display: contents;
}

.reorder-element {
    height: 52px;
}

.reorder-element td {
    border-bottom: thin solid
        rgba(var(--v-border-color), var(--v-border-opacity));
    padding: 0 40px 0 10px;
}
</style>
